import { Component, ElementRef, forwardRef, Input, AfterViewInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import Quill from 'quill';
import 'quill/dist/quill.snow.css'; // CSS, without this, it doesn't work

// TODO: Giant images = nothing works on them
@Component({
  selector: 'app-quill-editor',
  standalone: true,
  templateUrl: './quill-editor.component.html',
  styleUrls: ['./quill-editor.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => QuillEditorComponent),
      multi: true
    }
  ]
})
export class QuillEditorComponent implements ControlValueAccessor, AfterViewInit {
  @Input() placeholder: string = "Please enter details ...";

  private quillInstance!: Quill;
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  // So that Validation.required works
  // Holds initial value until Quill is ready
  // The component itself has no way of knowing the initial value from the form until writeValue() gets called
  // writeValue() is called by Angular Forms before the Quill editor actually exists (because ngAfterViewInit happens later).
  // That’s why the initial value isn’t being set correctly without this
  private initialValue: string = '';

  constructor(private el: ElementRef) {}

  ngAfterViewInit() {
    this.initQuill();
  }

  private initQuill() {
    this.quillInstance = new Quill(this.el.nativeElement.querySelector('#editor'), {
      theme: 'snow',
      placeholder: this.placeholder,
      modules: {
        toolbar: [
          [{ header: [1, 2, 3, 4, 5, 6, false] }], // Headers
          [{ 'size': ['small', false, 'large', 'huge'] }], // Font size
          ['bold', 'italic', 'underline', 'strike'],
          [{ color: [] }, { background: [] }],
          ['blockquote', 'code-block'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ direction: 'rtl' }],
          [{ align: [] }],
          // ['link', 'image', 'video/'] // Remove video for now (need an iframe)
          // ['link'], 
          ['image']
        ]
      }
    });

    // Handle images when they are inserted (this is where you resize)
    const toolbar = this.quillInstance.getModule('toolbar') as any;  // Quill doesn't expose proper types for modules
    toolbar.container.addEventListener('click', (event: Event) => {
      if ((event.target as HTMLElement).classList.contains('ql-image')) {
        setTimeout(() => this.resizeAllImagesInEditor(), 100);
      }
    });


    // Apply initial content
    this.quillInstance.root.innerHTML = this.initialValue;

    // Sync content to form
    this.quillInstance.on('text-change', () => {
      // const htmlContent = this.quillInstance.root.innerHTML;
      // Can't trust the users (e.g. resize giant images that get dropped from the clipboard instead of a URL, prevent injection, etc.)
      // This only changes in the editor while the user is editing, it doesn't affect the backend.
      const sanitizedHtmlContent = this.sanitizeAndExtractContent();
      this.onChange(sanitizedHtmlContent);
    });

    // Mark touched
    this.quillInstance.root.addEventListener('blur', () => this.onTouched());
  }

  writeValue(value: string): void {
    this.initialValue = value || ''; // For Validation.required
    if (this.quillInstance) {
      this.quillInstance.root.innerHTML = this.initialValue;
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  private sanitizeAndExtractContent(): string {
    const content = this.quillInstance.root.innerHTML;

    const doc = new DOMParser().parseFromString(content, 'text/html');
    doc.querySelectorAll('img').forEach(img => {
      img.style.maxWidth = '100%';
      img.style.height = 'auto';
    });

    return doc.body.innerHTML;
  }

  private resizeAllImagesInEditor() {
    this.quillInstance.root.querySelectorAll('img').forEach((img: HTMLImageElement) => {
      img.style.maxWidth = '100%';
      img.style.height = 'auto';
    });
  }
}
